/**
 * Audit Service for the Authoring App
 * Requirements: 19.1, 19.2, 19.4
 *
 * Provides integration between app actions and the audit log system.
 * Hooks into Editor Store mutations, workflow transitions, and auth events.
 */

import {
  logAction,
  type AuditAction,
  type AuditResourceType,
  type AuditLogEntry,
  type LogActionInput,
} from '@course-authoring/shared-types';

/**
 * Context for audit logging - provides user info
 */
export interface AuditContext {
  userId: string;
}

let currentAuditContext: AuditContext | null = null;

export function setAuditContext(context: AuditContext | null): void {
  currentAuditContext = context;
}

export function getAuditContext(): AuditContext | null {
  return currentAuditContext;
}

export function logWithContext(
  action: AuditAction,
  resourceType: AuditResourceType,
  resourceId: string,
  options?: {
    previousState?: unknown;
    newState?: unknown;
    metadata?: Record<string, unknown>;
  }
): AuditLogEntry | null {
  if (!currentAuditContext) {
    console.warn('Audit logging skipped: no audit context set');
    return null;
  }

  return logAction({
    userId: currentAuditContext.userId,
    action,
    resourceType,
    resourceId,
    previousState: options?.previousState,
    newState: options?.newState,
    metadata: options?.metadata,
  });
}

export function logUserLogin(
  userId: string,
  metadata?: Record<string, unknown>
): AuditLogEntry {
  return logAction({
    userId,
    action: 'login',
    resourceType: 'user',
    resourceId: userId,
    metadata,
  });
}

export function logUserLogout(userId: string): AuditLogEntry {
  return logAction({
    userId,
    action: 'logout',
    resourceType: 'user',
    resourceId: userId,
  });
}

export function logTokenRefresh(userId: string): AuditLogEntry {
  return logAction({
    userId,
    action: 'token_refresh',
    resourceType: 'user',
    resourceId: userId,
  });
}

export type { AuditLogEntry, LogActionInput };
